import { observable, runInAction, action, computed, reaction, extendObservable, toJS } from 'mobx';
import { paperworkEntryService } from 'modules/paperwork-entry';

class BaseListQueryStore {
    fetchFunc = null;

    constructor(fetchFunc) {
        this.fetchFunc = fetchFunc;
        this.reset();
        this.clear();

        reaction(
            () => toJS(this.filter),
            (filter) => filter.pageNumber && filter.pageSize && this.fetchCollection()
        )
    }

    @action
    reset() {
        extendObservable(this, {
            entries: [],
            loading: false,
            error: null,
            successMessage : null,
            totalRecordCount: 0,
            filter: {},
            selectedForPrint : []
        })
    }

    @action
    clearMessages() {
        this.error = null;
        this.successMessage = null;
    }   

    @action
    setEntries(entries) {
        this.markAsChecked(entries);
        this.entries = entries;
    }

    @action
    markAsChecked(entries) {
        const selectedForPrint = this.selectedForPrint;

        entries.forEach(function(entry) {
            if(selectedForPrint)
            {
                var index = selectedForPrint.indexOf(entry.id)
                if(index != -1)
                {
                    let entryIndex = entries.indexOf(entry)
                    if(entryIndex != -1)
                    {
                        console.log(entryIndex);
                        entries[entryIndex].selectedForPrint = true;
                    }
                }
            }        
        });
    }

    @action
    setLoading(loading) {
        this.loading = loading;
    }

    @action
    setError(error) {
        this.error = error;
    }

    @action
    setSuccessMessage(successMessage) {
        this.successMessage = successMessage;
    }

    @action
    setPage(pageNumber) {
        this.filter.pageNumber = pageNumber;
    }

    @action
    setPageSize(pageSize) {
        this.filter.pageSize = pageSize;
    }

    @action
    setTotalRecordCount(totalRecordCount) {
        this.totalRecordCount = totalRecordCount;
    }

    @action
    setFilter(filter) {
        this.filter = filter;
    }

    @action
    setSort(sort) {
        this.filter.sort = sort;
    }

    @action
    setEmbeds(embeds) {
        this.filter.embeds = embeds;
    }

    @action
    setDontClearFlag(bool) {
        this.dontClearFlag = bool
    }

    @action
    clear() {
        var pageNumber = 1;
        if(this.dontClearFlag == true) {
            pageNumber =  this.filter.pageNumber
        }    
        this.setFilter({
            ...this.filter,
            pageNumber: pageNumber,
            pageSize: 10
        });
        this.setTotalRecordCount(0);
        this.selectedForPrint = [];
        this.setDontClearFlag(false);
    }

    @action
    async fetchCollection() {
        this.setLoading(true);    
        try {
            const response = await this.fetchFunc(this.filter);
            this.handleResponseCollection(response);
        } catch(error) {
            this.setError(error);
        }        

        this.setLoading(false);
    }

    @action
    handleResponseCollection(response) {
        const { totalRecordCount, records } = response;
        this.setEntries(records);
        this.setTotalRecordCount(totalRecordCount);
    }

    @action
    selectEntryForPrint(id, value) {
        var entryIndex = this.entries.map(function(e) { return e.id; }).indexOf(id);
        var selectedIndex = this.selectedForPrint.indexOf(id);

        if(value === true) {
            if(selectedIndex === -1) {
                this.selectedForPrint.push(id);
                if(entryIndex !== -1) {
                this.entries[entryIndex].selectedForPrint = true;
                }
            }
        }
        else {
            this.selectedForPrint.splice(selectedIndex, 1);
            if(entryIndex !== -1) {
                this.entries[entryIndex].selectedForPrint = false;
            }
        }
    }
}

class PaperworkEntryStore extends BaseListQueryStore {
    constructor() {
        super(paperworkEntryService.find.bind(paperworkEntryService));        
    }

    @observable loadingImages = false;

    @action
    setLoadingImages(loadingImages) {
        this.loadingImages = loadingImages;
    }

    @action
    reset() {
        super.reset();
        extendObservable(this, {
            paperworkEntry: null,
            images: [],
            image: null,
            paperworkEntriesForPrint : [],
            paperworkStatusLookup : []            
        })
        this.setEmbeds('user,paperworkstatus,client');
        this.setSort('createdOn|desc');

    }

    @computed get tags() {
        return this.paperworkEntry && this.paperworkEntry.paperworkEntryTags ? this.paperworkEntry.paperworkEntryTags.map(e => e.tag) : null;
    }

    @action
    setPaperworkEntry(entry) {
        this.paperworkEntry = entry;
    }

    @action
    setPaperworkEntryHistory(paperworkEntryHistory) {
        this.paperworkEntryHistory = paperworkEntryHistory;
    }

    @action
    setPaperworkEntriesForPrint(paperworkEntriesForPrint) {
        this.paperworkEntriesForPrint = paperworkEntriesForPrint;
    }

    @action
    setImages(images) {
        this.images = images;
    }

    @action
    setImage(image) {
        this.image = image;
    }

    @action
    async getPaperworkEntry(id) {
        this.setLoading(true);
        try {
            const entry = await paperworkEntryService.get({
                id,
                embeds: 'user,paperworkImageEntries,paperworkEntryTags,client,paperworkStatus'
            });

            this.setPaperworkEntry(entry);
            this.setLoading(false);
            await this.getPaperworkEntryImages(entry.paperworkImageEntries);
        } catch(error) {
            this.setLoading(false);
            this.setError('Unable to fetch paperwork entry');
        }                 
    }    

    @action
    async getPaperworkEntryHistory(id) {
        this.setLoading(true);
        try {
            const entryHistory = await paperworkEntryService.getHistory({
                paperworkEntryId: id,
                embeds: 'oldstatus,newStatus,user'
            });
            this.setPaperworkEntryHistory(entryHistory);
            this.setLoading(false);
        } catch(error) {
            this.setLoading(false);
            this.setError('Unable to fetch paperwork entry');
        }                 
    }    

    @action
    async getPaperworkEntryForEdit(id) {
        try {
            this.setLoading(true);
           
            const entry = await paperworkEntryService.get({
                id,
                embeds: 'user,paperworkImageEntries,paperworkEntryTags,client,paperworkStatus'
            });

            this.setPaperworkEntry(entry);
            this.setLoading(false);
        } catch(error) {
            this.setLoading(false);
            this.setError('Unable to fetch paperwork entry');
        }                 
    }    

    @action
    async getPaperworkEntryImages(entries) {
        this.setLoadingImages(true);
        try {
            var results = await Promise.all(entries.map(async (entry) => {
                var result = await paperworkEntryService.getImageUrl(entry.id);
                var img = new PaperworkImageEntry(entry.id, entry.name, result.data, result.options.size, result.element);
                img.setPromise(async () => { 
                    img.setLoading(true);
                    img.setPromiseResult(await paperworkEntryService.getImageUrl(entry.id, false));
                    img.setLoading(false);
                })
                return img;
            }));
            this.setImages(results);
        } catch(error) {
            this.setError('Unable to fetch paperwork entry images!');
        }  
        this.setLoadingImages(false);
    }

    @action
    async getPaperworkEntryImage(id) {
        this.setLoading(true);
        try {
            var img = await paperworkEntryService.getPaperworkImage({ id });
            var result = await paperworkEntryService.getImageUrl(id);
            var image = new PaperworkImageEntry(id, img.name, result.data, result.options.size, result.element, img.text);
            this.setImage(image);
        } catch (error) {
            this.setError(error);
        }
        this.setLoading(false);
    }

    @action
    async updatePaperworkEntryImage(entry) {
        this.setLoading(true);
        try {
            var result = await paperworkEntryService.updatePaperworkImage(entry);
            this.setSuccessMessage("Sucessfully updated image data.");
        } catch (error) {
            this.setError("An error occured while updating image data.");
        }
        this.setLoading(false);
    }

    @action
    async updatePaperworkEntryStatus(id, data) {
        this.setLoading(true);
        try {
            await paperworkEntryService.updatePaperworkEntryStatus(id, data);
            this.setSuccessMessage("Sucessfully updated paperwork entry.");
        } catch (error) {
            this.setError("An error occured while updating paperwork entry.");
        }
        this.setLoading(false);
    }

    @action
    async getPaperworkPrintData() {
        var entries = [];
        this.setLoading(true);
        try {
            for (const id of this.selectedForPrint) {
                const entry = await paperworkEntryService.get({
                    id,
                    embeds: 'user,paperworkImageEntries,paperworkEntryTags'
                });
    
                await Promise.all(entry.paperworkImageEntries.map(async (file) => {
                    var result = await paperworkEntryService.getImageUrl(file.id);
                    file.url = result.data;
                  }));
                  entries.push(entry);
              }

            this.setPaperworkEntriesForPrint(entries);
            this.setLoading(false);
        } catch(error) {
            this.setLoading(false);
            this.setError('An error occurred while fetching paperwork print data.');
        }                  
    }   

    @action
    async createPaperworkEntryPrintLog(ids) {
        try {
            await paperworkEntryService.createPaperworkEntryPrintLog(ids);
        } catch(error) {
        }                 
    }    

    @action
    async getPdfDocument(ids) {
        try {
            return await paperworkEntryService.getPdfDocument(ids);
        } catch(error) {
        }                 
    }   

    @action
    async getPaperworkStatuses(includeFilterValues) {
        try {
            this.setLoading(true);
            const paperworkStatusLookup = await paperworkEntryService.getPaperworkStatuses(includeFilterValues);
            this.setPaperworkStatuses(paperworkStatusLookup);
            this.setLoading(false);
        } catch(error) {
        }                 
    }  

    @action
    setPaperworkStatusFilter(value) {
        this.filter.status = value;
    }

    @action
    setPaperworkStatusSelectedValue(value) {
        this.defaultStatusLookupSelectionValue = value;
    }

    @action
    setPaperworkStatuses(paperworkStatusLookup) {
        this.paperworkStatusLookup = paperworkStatusLookup;  
        this.setPaperworkStatusSelectedValue(paperworkStatusLookup[0]);
    }

}

class PaperworkImageEntry {
    @observable name = null;
    @observable url = require("assets/images/thumb.png");
    @observable loading = false;
    width = 9;
    height = 16;
    @observable element = null;
    @observable promiseResult = null;
    promise = null;

    @action setPromise(promise) {
        this.promise = promise;
    }

    @action setPromiseResult(result) {
        this.promiseResult = result;
    }

    @computed get src() {
        return this.url || '';
    }

    @action
    setLoading(loading) {
        this.loading = loading;        
    }

    @action
    setUrl(url) {
        this.url = url;
    }

    promise = null;

    constructor(id, name, url, size, element, text) {
        this.id = id;
        this.key = id;
        this.name = name;
        this.url = url;
        this.width = size.width;
        this.height = size.height;
        this.element = element;
        this.text = text;
    }
}

const paperworkEntryStore = new PaperworkEntryStore();
export { paperworkEntryStore }


// WEBPACK FOOTER //
// ./src/modules/paperwork-entry/paperworkEntryStore.js